import { useQueryNormalizer } from '@normy/react-query';
import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { gqlClient, queryClient } from '@/lib';
import {
  type AddToCalendarConfigAccess,
  AddToCalendarConfigKeys,
  type AddToCalendarConfigMode,
  InteractionAppNames,
  type InteractionAppConfigInput,
} from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import { usePageExperienceContext } from '../contexts/PageExperienceContext';
import type { PageExperience } from '../types';
import type {
  SetAddToCalendarConfigMutation,
  SetAddToCalendarConfigMutationVariables,
} from './useSetAddToCalendarConfig.generated';

const query = gql`
  mutation SetAddToCalendarConfig($input: InteractionAppConfigInput!) {
    configureInteractionApp(input: $input) {
      id
      applicationName
      config {
        setting
        value
      }
    }
  }
`;

type Options = MutateOptions<
  SetAddToCalendarConfigMutation,
  QueryError,
  SetAddToCalendarConfigMutationVariables
>;

/**
 * Writes a setting on the experience's Add to Calendar app.
 *
 * One mutation for the whole app rather than one per setting: they share an
 * endpoint, a cache key and an optimistic path, and the only thing that varies
 * is which config row is written. The exported setters are per-setting so each
 * call site keeps its own value type.
 *
 * Note the server admits an editor for `mode` but only the OWNER for `access`.
 */
export const useSetAddToCalendarConfig = () => {
  const { experience } = usePageExperienceContext();
  const queryNormalizer = useQueryNormalizer();

  const { mutate, isPending } = useMutation<
    SetAddToCalendarConfigMutation,
    QueryError,
    SetAddToCalendarConfigMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        SetAddToCalendarConfigMutation,
        SetAddToCalendarConfigMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      if (experience) {
        queryClient.invalidateQueries({
          queryKey: createExperienceQueryKey(experience.id),
          exact: true,
        });
      }
    },
    onMutate: (variables) => {
      const { experienceId } = variables.input;
      const prevValue =
        queryNormalizer.getObjectById<PageExperience>(experienceId);
      const { setting, value } = variables.input.config[0];

      return {
        rollbackData: {
          interactionApps: prevValue?.interactionApps,
        },
        optimisticData: {
          interactionApps: prevValue?.interactionApps.map((app) => {
            if (app.applicationName !== InteractionAppNames.AddToCalendar) {
              return app;
            }
            // An app cached before a setting existed carries no row for it, so
            // append rather than assuming there is one to replace.
            const hasSetting = app.config.some(
              (config) => config.setting === setting
            );
            return {
              ...app,
              config: hasSetting
                ? app.config.map((config) =>
                    config.setting === setting ? { ...config, value } : config
                  )
                : [...app.config, { setting, value }],
            };
          }),
        },
      };
    },
  });

  const setConfig = useCallback(
    (setting: AddToCalendarConfigKeys, value: string, options?: Options) => {
      const interactionApp = experience.interactionApps.find(
        (app) => app.applicationName === InteractionAppNames.AddToCalendar
      );

      if (!interactionApp) {
        console.warn(
          '[configureInteractionApp] failed to find an interaction app'
        );
        return;
      }

      const input: InteractionAppConfigInput = {
        experienceId: experience.id,
        interactionAppId: interactionApp.id,
        config: [{ setting, value }],
      };
      mutate({ input }, options);
    },
    [mutate, experience]
  );

  /** Set how Add to Calendar behaves for all events on the experience. */
  const setAddToCalendarMode = useCallback(
    (value: AddToCalendarConfigMode, options?: Options) =>
      setConfig(AddToCalendarConfigKeys.Mode, value, options),
    [setConfig]
  );

  /** Set who the experience offers Add to Calendar to. Owner-only server-side. */
  const setAddToCalendarAccess = useCallback(
    (value: AddToCalendarConfigAccess, options?: Options) =>
      setConfig(AddToCalendarConfigKeys.Access, value, options),
    [setConfig]
  );

  /**
   * Set whether the app cascades onto the planner's child views, which is what
   * draws its controls onto the event cards in the page.
   */
  const setAddToCalendarInherited = useCallback(
    (value: boolean, options?: Options) =>
      setConfig(AddToCalendarConfigKeys.IsInherited, String(value), options),
    [setConfig]
  );

  return {
    setAddToCalendarMode,
    setAddToCalendarAccess,
    setAddToCalendarInherited,
    isPending,
  };
};
